// library imports
import {
  Card,
  Segmented,
  Carousel,
  Skeleton,
  Flex,
  Row,
  Col,
  Grid,
} from "antd";
import { useEffect, useState, useContext } from "react";
import dayjs from "dayjs";
// style imports
import "../../styles/Dashboard/DashboardPage.css";
// component imports
import WeatherCard from "../../components/dashboard/WeatherCard";
import SystemInformationCard from "../../components/dashboard/SystemInformationCard";
import SummaryCard from "../../components/dashboard/SummaryCard";
import StatisticGraph from "../../components/dashboard/StatisticGraph";
import PowerEnergyCard from "../../components/dashboard/PowerEnergyCard";
import PVConditionContent from "../../components/dashboard/PVConditionContent";
import AlarmContent from "../../components/dashboard/AlarmContent";
import SunHourContent from "../../components/dashboard/SunHourContent";
import HeatmapContent from "../../components/dashboard/HeatmapContent";
import SocChartContent from "../../components/dashboard/SocChartContent";
// context imports
import { IndexContext } from "../../context/IndexContext";

const { useBreakpoint } = Grid;

const powerEnergyDateTypeOptions = [
  { label: "Day", value: "day" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
];

// TODO: some charts can be changed to the more general one, so no need so many charts like PvEnergyChart, OverallEnergyChart ...
// FullDoughnutChart and EmptyDoughnutChart are temporary solution, later on can try to modify DoughnutChart to make it more universal
export default function DashboardPage() {
  const {
    hasPvPowerMeter,
    hasInverterSelected,
    subscribeAiBess,
    subscribeAiPvDiagnosis,
    getHasInverterSelectedLoading,
    getFeatureSubscriptionLoading,
    isLightTheme,
  } = useContext(IndexContext);

  const screens = useBreakpoint();

  // date selection for power and energy demand chart
  const [powerEnergyDateSelection, setPowerEnergyDateSelection] =
    useState(dayjs());
  // date type selection for power and energy demand chart
  const [powerEnergyDateTypeSelection, setPowerEnergyDateType] = useState(
    powerEnergyDateTypeOptions[0].value,
  );

  // pv condition alarm option
  const pvConditionAlarmRadioOptions = [
    { label: "Alarm", value: "alarm" },
    { label: "PV Condition", value: "pvcondition" },
  ];
  const [pvConditionAlarmRadioSelection, setPvConditionAlarmRadioSelection] =
    useState(pvConditionAlarmRadioOptions[0].value);

  // carousel card height
  const [carouselCardHeight, setCarouselCardHeight] = useState(0);

  const [refreshCheck, setRefreshCheck] = useState(false);
  const [checkDate, setCheckDate] = useState(
    dayjs().format("YYYY-MM-DD HH:mm"),
  );

  useEffect(() => {
    if (refreshCheck) {
      setCheckDate(dayjs().format("YYYY-MM-DD HH:mm"));
      setRefreshCheck(false);
    }
  }, [refreshCheck]);

  useEffect(() => {
    setCarouselCardHeight(
      document.getElementById("dashboard-carousel-card")?.clientHeight,
    );
  }, [
    document.getElementById("dashboard-carousel-card")?.clientWidth,
    document.getElementById("dashboard-carousel-card")?.clientHeight,
  ]);

  return (
    <Row gutter={[8, 8]}>
      <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={6}>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <SystemInformationCard
              containerID={"dashboard-system-information-graph"}
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <SummaryCard />
          </Col>
        </Row>
      </Col>

      <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={18}>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <StatisticGraph />
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
            <PowerEnergyCard
              powerEnergyDateSelection={powerEnergyDateSelection}
              setPowerEnergyDateSelection={setPowerEnergyDateSelection}
              powerEnergyDateTypeSelection={powerEnergyDateTypeSelection}
              setPowerEnergyDateType={setPowerEnergyDateType}
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                <Card
                  size="small"
                  style={
                    screens.lg || screens.xxl
                      ? {
                          height:
                            "calc((100vh - 58px - 50px - 24px - 126px)/2)",
                        }
                      : undefined
                  }
                  styles={
                    screens.lg || screens.xxl
                      ? { body: { height: "100%" } }
                      : undefined
                  }
                  id="dashboard-carousel-card"
                >
                  {!getHasInverterSelectedLoading &&
                  !getFeatureSubscriptionLoading ? (
                    <Carousel
                      arrows
                      autoplay={false}
                      dotPosition="bottom"
                      dots={{
                        className: isLightTheme ? "bgPrimary" : "bgPrimaryDark",
                      }}
                      className={
                        isLightTheme ? "carousel-light" : "carousel-dark"
                      }
                      infinite={false}
                      adaptiveHeight={true}
                    >
                      {hasPvPowerMeter ||
                        (hasInverterSelected && (
                          <SunHourContent
                            parentContainerHeight={carouselCardHeight}
                            dateSelected={powerEnergyDateSelection}
                            dateType={powerEnergyDateTypeSelection}
                          />
                        ))}

                      {hasInverterSelected ? (
                        <div>
                          <HeatmapContent
                            parentContainerHeight={carouselCardHeight}
                            dateSelected={powerEnergyDateSelection}
                            dateType={powerEnergyDateTypeSelection}
                          />
                        </div>
                      ) : null}

                      {subscribeAiBess ? (
                        <div>
                          <SocChartContent
                            parentContainerHeight={carouselCardHeight}
                            powerEnergyDateSelection={powerEnergyDateSelection}
                            powerEnergyDateTypeSelection={
                              powerEnergyDateTypeSelection
                            }
                          />
                        </div>
                      ) : null}
                    </Carousel>
                  ) : (
                    <Skeleton
                      active={true}
                      style={{
                        padding: "1rem",
                        maxWidth: "70%",
                        margin: "auto",
                      }}
                      paragraph={{ rows: 5 }}
                    />
                  )}
                </Card>
              </Col>

              <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                <Card
                  size="small"
                  style={{
                    height:
                      screens.lg || screens.xxl
                        ? "calc((100vh - 58px - 50px - 24px - 126px)/2)" // 100vh - nav menu height - site selection height - gap - statistic graph height
                        : "calc((100vh - 50px)/2)",
                  }}
                  styles={
                    screens.lg || screens.xxl
                      ? { body: { height: "calc(100% - 38px)" } } // 100% - card header height
                      : { body: { height: "70%" } }
                  }
                  title={
                    <Flex justify="space-between" align="center">
                      {subscribeAiPvDiagnosis ? (
                        <Segmented
                          options={pvConditionAlarmRadioOptions}
                          onChange={(value) => {
                            setPvConditionAlarmRadioSelection(value);
                          }}
                          value={pvConditionAlarmRadioSelection}
                        />
                      ) : (
                        <div>Alarm</div>
                      )}

                      <span className="condition-alarm-card-title-span">
                        Last Check:{" "}
                        <span>
                          <b>{checkDate}</b>
                        </span>
                      </span>
                    </Flex>
                  }
                >
                  {pvConditionAlarmRadioSelection == "pvcondition" ? (
                    <PVConditionContent setRefreshCheck={setRefreshCheck} />
                  ) : (
                    <AlarmContent setRefreshCheck={setRefreshCheck} />
                  )}
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
